import update, {extend} from 'immutability-helper'
import moment from 'moment'
import {omit} from 'lodash'
import {at} from 'lodash'
import React, {createContext} from 'react'

import {profile} from 'helpers/api'
import {delay} from 'helpers/utils'
import {register} from 'helpers/hooks'
import triggerHooks from 'helpers/hooks'


import * as fetch from 'context/fetch'
import registerContextHooks from 'context/hooks'
import quizContext from 'context/quiz-context'
import rollbookContext from 'context/rollbook-context'
import preferencesContext from 'context/preferences-context'

import standardsContext from 'teacher/components/add-standards/context'
import standardsHooks, * as standardsFetches from 'teacher/components/add-standards/hooks'

import homeworkContext from 'teacher/homework/context'
import homeworkHooks, * as homeworkFetches from 'teacher/homework/hooks'

import {inViewer} from 'helpers/url'
import {getEdgeServers} from 'helpers/api'
import EdgePermissionBanner from 'components/EdgePermissionBanner'
import {pingEdgeServers, grantEdgePermission, skipEdgePermission} from 'helpers/edge'

window.downloadedContentCB = undefined
window.downloadedContent = {}
window.setDownloadedContent = raw => {
  const content = raw.reduce((acc, {id, hash}) => ({...acc, [id]: hash}), {})
  window.downloadedContent = content
  if (window.downloadedContentCB) window.downloadedContentCB(content)
}

export const initialState = {
  displayingScheduleMeeting: false,
  openfireSettings: false,
  openfireSettingsLoaded: false,
  downloadedContent: {},
  unreadMessages: 0,
  assessments: [],
  assessmentUpdates: 0,
  assessmentView: [],
  assessmentViewUpdates: 0,
  assessmentCount: 0,
  sizes: {width: window.innerWidth, height: window.innerHeight},
  sending: {
    scheduleAssessment: false
  },
  news: [],
  units: [],
  lessons: [],
  myLessons: [],
  activity: [],
  groups: [],
  groupsLoaded: false,
  groupsDigests: {},
  students: {},
  eventUpdates: 0,
  uploadUpdates: 0,
  scheduledDays: [],
  events: [],
  eventsByDay: {},
  groupsLanguage: false,
  catalogueSubjects: [],
  catalogueCourses: [],
  catalogueSearchResults: [],
  downloadLog: [],
  studentsDownloadLog: [],
  lessonLogTitles: {},
  downloadLogFilters: [],
  loading: {},
  selectedLesson: {},
  goingBack: false,
  menuShowing: false,
  expandedMenuShowing: false,
  myCourses: [],
  mySubjects: [],
  myGrades: [],
  newPlan: {},
  planUpdated: false,
  suggestedGroup: undefined,
  suggestedDate: moment().toISOString(),
  planningWeek: moment().startOf('week').format('YYYY-MM-DD'),
  planningNavigatedFrom: "week",
  planningNavigatedFromQuery: {},
  newPlanFrom: "week",
  newPlanFromQuery: {},
  planTemplates: [],
  templateItems: [],
  templateUpdates: 0,
  plans: [],
  plansUpdated: false,
  planUpdates: 0,
  plansByDay: {},
  setState: (p) => {},
  deSubjects: [],
  deGrades: [],
  deStandards: [],
  deExpectations: [],
  pdfPlans: [],
  pdfPlansLoaded: false,
  teacherStandards: {},
  teacherExpectations: {},
  teacherStandardsLoaded: false,
  teacherExpectationsLoaded: false,
  cycles: [],
  cycleUpdates: 0,
  searchBackPath: '',
  ...quizContext,
  ...preferencesContext,
  ...rollbookContext,
  standardsContext,
  homeworkContext,
  dashboard: {
    loading: false,
    activeVideo: null,
    videos: []
  },
  edgeServers: {init: false, available: false, ips: [], needsPermission: false},
  showEdgeBanner: false,
}

const Context = createContext()
const {Provider, Consumer} = Context

function mapSizes ([e]) {
  const {innerHeight: height, innerWidth: width} = e.target
  return {sizes: {height, width}}
}

function buildContext (props, state) {
  return {...omit(props, ['children']), ...state}
}

const edusystem = window.edusystem = window.edusystem = {}
const edusystemChat = edusystem.chat = edusystem.chat = {}

class AppProvider extends React.Component {

  constructor (props) {
    super(props)
    const setState = this.setState.bind(this)
    this.state = {
      ...initialState,
      setState,
      updateState: updateState.bind(this),
      at: (...paths) => at(this.state, paths),
      isDownloaded: ({id, hash}) => inViewer && (this.state.downloadedContent[id] == hash)
    }

    function updateState (...commands) {
      return new Promise((resolve, reject) => {
        try {
          const state = commands.reduce((state, command) => update(state, command), this.state)
          setState(state, () => resolve(state))
        } catch (e) {
          reject(e)
        }
      })
    }

    this.setSizes = delay(200, setState, mapSizes).bind(this)
    this.setUnreadMessages = unreadMessages => setState({unreadMessages})

    this.allowEdgeAccess = async () => {
      grantEdgePermission()
      this.state.updateState({showEdgeBanner: {$set: false}})
      try {
        const loadedProfile = await profile
        const edgeServers = await pingEdgeServers(loadedProfile)
        if (this._mounted) this.state.updateState({edgeServers: {$set: edgeServers}})
      } catch(e) {
        console.error('[edge] allowEdgeAccess failed:', e)
      }
    }

    this.skipEdgeAccess = () => {
      skipEdgePermission()
      this.state.updateState({
        showEdgeBanner: {$set: false},
        edgeServers: {$set: {init: true, available: false, ips: [], needsPermission: false}}
      })
    }

 }

  componentDidMount() {
    this._mounted = true

    window.addEventListener('resize', this.setSizes)

    if(window.downloadedContent)
      this.state.updateState({downloadedContent: {$set: window.downloadedContent}})
    window.downloadedContentCB = content => this.state.updateState({downloadedContent: {$set: content}})

    edusystemChat.onUnreadMessagesChange = this.setUnreadMessages
    const context = buildContext(this.props, this.state)
    Object.values(fetch).forEach(fetch => fetch(context))
    Object.values(standardsFetches).forEach(fetch => typeof(fetch)== 'function' && fetch(context))
    Object.values(homeworkFetches).forEach(fetch => typeof(fetch)== 'function' && fetch(context))
    registerContextHooks()
    standardsHooks.forEach(({paths, fetches}) => register(paths, fetches));
    homeworkHooks.forEach(({paths, fetches}) => register(paths, fetches));

    getEdgeServers().then(edgeServers => {
      this.state.updateState({
        edgeServers:    {$set: edgeServers},
        showEdgeBanner: {$set: edgeServers.needsPermission}
      })
    })
  }

  componentDidUpdate(props, state) {
    triggerHooks(buildContext(this.props, this.state), buildContext(props, state))
  }

  componentWillUnmount() {
    this._mounted = false
    edusystemChat.onUnreadMessagesChange = null
    window.removeEventListener('resize', this.setSizes)
  }

  render() {
    const context = buildContext(this.props, this.state)
    return (
      <Provider value={context}>
        {this.state.showEdgeBanner && (
          <EdgePermissionBanner
            onAllow={this.allowEdgeAccess}
            onSkip={this.skipEdgeAccess}
          />
        )}
        {this.props.children}
      </Provider>
    )
  }
}

extend('$auto', function(value, object) {
  return object ?
    update(object, value):
    update({}, value)
}) 
extend('$autoArray', function(value, object) {
  return object ?
    update(object, value):
    update([], value)
})

export {AppProvider as default, Consumer, Context}
